import { addListener } from "~/lib/signals";
import Wasm, { TWasmMapClick } from "./wasm";
import WasmStoreMapping from "./mapping";
import { IXYPosition, TWasmId } from "~/interfaces";
import { createBtn, getImageCanvas, getRandomIcon } from "./utils";
import { IStoreItem } from "./store/interfaces";
import WasmStoreMarker from "./store/wasm-store-marker";
import WasmStoreGeometry from "./store/wasm-store-geometry";
import ContextMenu from "~/ui/contextmenu";
import ContextMenuItem from "~/ui/contextmenuitem";
import createSDSIcon from "~/ui/icon";
import { ICON_EXCLAMATION_CIRCLE_24 } from "~/ui/icons";
import { watch, watchCoordinates } from "~/state/util";
import { store } from "~/state/store";
import { setCoordinates } from "~/state/map/state";

type TMode = "normal" | "add" | "remove" | "dad";

const DEF_LINE = [16.554371068753948, 49.17660753654313, 16.558043044094642, 49.175690234731555, 16.55805700597811, 49.173777993714204];

function getLine(offset = 0) {
	return DEF_LINE.map(item => item + offset);
}

class WasmController {
	_selector: string;

	_canvas: HTMLCanvasElement;

	_wasm: Wasm;

	_mapping: WasmStoreMapping;

	_lastClick: IXYPosition;

	_dadData: null | {
		wasmId: TWasmId;
		id: IStoreItem["id"];
		marker: WasmStoreMarker;
	};

	_dadTo: number;

	_mode: TMode;

	_markers: Array<WasmStoreMarker>;

	_geometries: Array<WasmStoreGeometry>;

	_dom: {
		select: HTMLSelectElement;
		fps: HTMLSpanElement;
	};

	_afterLoad: boolean;

	constructor() {
		this._selector = "wasm-canvas";
		this._lastClick = {
			x: 0,
			y: 0,
		};
		this._dadData = null;
		this._markers = [];
		this._geometries = [];
		this._dadTo = 0;
		this._afterLoad = false;

		this._buildUI();
		this.mode = "dad";
		this._build();
		this._run();
	}

	set mode(mode: TMode) {
		this._mode = mode;
		this._dom.select.value = this._mode;
	}

	_buildUI() {
		const controls = document.createElement("div");

		controls.classList.add("wasm-controls");

		const select = document.createElement("select");
		const opts: Array<TMode> = ["normal", "add", "remove", "dad"];

		for (const opt of opts) {
			const option = document.createElement("option");

			option.value = opt;
			option.textContent = opt;
			select.append(option);
		}

		controls.append(select);
		document.body.append(controls);

		select.addEventListener("change", () => {
			this._mode = select.value as TMode;
		});

		const clearAllBtn = createBtn("Clear all", () => {
			this._clearAll();
		});
		const testBtn = createBtn("Add line", () => {
			this._test();
		});
		const test2Btn = createBtn("Update img", () => {
			this._test2();
		});
		const test3Btn = createBtn("Screenshot", () => {
			this._test3();
		});
		const test4Btn = createBtn("Active", () => {
			this._test4();
		});
		const test5Btn = createBtn("Mapset", () => {
			this._wasm.setMapstyle("ophoto", ["panoLayer"]);
		});
		const test6Btn = createBtn("Center", () => {
			this._wasm.setCenterZoom(17.026380, 48.8627845);
		});
		const fps = document.createElement("span");

		controls.append(clearAllBtn, testBtn, test2Btn, test3Btn, test4Btn, test5Btn, test6Btn, fps);

		this._dom = {
			select,
			fps,
		};
	}

	_build() {
		const canvas = document.createElement("canvas");

		canvas.classList.add(this._selector);
		canvas.style.width = `100%`;
		canvas.style.height = `100%`;

		const wasmParent = document.getElementById("wasm");

		wasmParent.append(canvas);
		document.getElementById("left3d").style.display = "none";
		document.getElementById("map").style.display = "none";

		const wasm = new Wasm({
			parentElem: wasmParent,
			canvasQuery: `.${this._selector}`,
			onMapClick: data => {
				this._onMapClick(data);
			},
			onFps: fps => {
				if (this._dom.fps) {
					this._dom.fps.textContent = fps.toString();
				}
			},
			onHover: (wasmId, type) => {
				this._mapping?.propagateHoverEvent(wasmId, type);
			},
			onContextMenu: evt => {
				this._contextMenu(evt);
			},
			onMapRedraw: () => {
				if (!this._afterLoad) {
					return;
				}

				const position = this._wasm.getCenterZoom();

				store.dispatch(setCoordinates({
					longitude: position.lon,
					latitude: position.lat,
					zoom: position.zoom,
				}));
				Mapy.getComponent("history").save();
			},
		});

		// update velikosti
		addListener({}, "width-change", () => {
			wasm.syncPort();
		});

		this._wasm = wasm;
		this._canvas = canvas;
	}

	async _run() {
		/* eslint-disable-next-line */
		const [loadData, canvasData] = await Promise.all([
			this._wasm.loadProm,
			getImageCanvas("/wasm/drop/big-red.png"),
		]);

		const { mapControl, module } = this._wasm.exports;

		mapControl.setTargetFps(60);

		this._wasm.setCustomTexture("custom", canvasData.setNumber(1));
		this._wasm.setCustomTexture("custom2", canvasData.setNumber(2));

		this._canvas.addEventListener("pointermove", event => {
			if (this._dadData) {
				const { clientX, clientY } = event as MouseEvent;
				const pos = mapControl.screenPosToMapPos({
					x: clientX,
					y: clientY,
				});
				const wgs = module.GeoMath.mercatorToWGS(pos);

				this._dadData.marker.update({
					lon: wgs.x,
					lat: wgs.y,
				});
			}
		});

		document.addEventListener("keydown", event => {
			switch (event.code) {
				case "Digit1":
					this.mode = "normal";
					break;

				case "Digit2":
					this.mode = "add";
					break;

				case "Digit3":
					this.mode = "remove";
					break;

				case "Digit4":
					this.mode = "dad";
					break;

				default:
			}
		});

		this._mapping = new WasmStoreMapping(this._wasm);
		this._addDefaultItems();
		this._watchForMapChange();
		this._afterLoad = true;
	}

	_watchForMapChange() {
		const curState = store.getState().map;
		const wt = watch(store.getState, "map", watchCoordinates);

		store.subscribe(wt((newMap, oldMap) => {
			if (!newMap.ignore
				&& (
					newMap.longitude !== oldMap.longitude
					|| newMap.latitude !== oldMap.latitude
					|| newMap.zoom !== oldMap.zoom
				)
			) {
				// neumime animace
				this._wasm.setCenterZoom(newMap.longitude, newMap.latitude, newMap.zoom);

				store.dispatch(setCoordinates({
					animating: false,
				}));
			}
		}));

		this._wasm.setCenterZoom(curState.longitude, curState.latitude, curState.zoom);
	}

	_addDefaultItems() {
		this._markers.push(new WasmStoreMarker({
			img: getRandomIcon(),
			lon: 16.556304789608227,
			lat: 49.17696806344632,
		}));
		this._markers.push(new WasmStoreMarker({
			lon: 16.55456653512202,
			lat: 49.17743811356799,
			type: "custom",
		}));
		this._markers.push(new WasmStoreMarker({
			lon: 16.55294533059762,
			lat: 49.177539777623025,
			type: "custom2",
		}));
		this._geometries.push(new WasmStoreGeometry({
			points: getLine(0),
		}));
		this._geometries.push(new WasmStoreGeometry({
			type: "red",
			points: getLine(0.0005),
		}));
		this._geometries.push(new WasmStoreGeometry({
			type: "green",
			points: getLine(0.0005 * 2),
		}));
		this._geometries.push(new WasmStoreGeometry({
			type: "route",
			points: getLine(0.0005 * 3),
		}));
	}

	_onMapClick(data: TWasmMapClick) {
		if (data.name === "element") {
			this._mapping.propagateClickEvent(data.wasmId);
		}

		switch (this._mode) {
			case "add": {
				if (data.name === "map") {
					const { clientX, clientY } = data.event;
					const { mapControl, module } = this._wasm.exports;

					const pos = mapControl.screenPosToMapPos({
						x: clientX,
						y: clientY,
					});
					const wgs = module.GeoMath.mercatorToWGS(pos);
					const marker = new WasmStoreMarker({
						img: getRandomIcon(),
						lon: wgs.x,
						lat: wgs.y,
					});

					this._markers.push(marker);
				}
				break;
			}

			case "remove":
				if (data.name === "element") {
					const dataId = this._mapping.getWasmToStoreId(data.wasmId);
					const marker = this._markers.filter(item => item.id === dataId)[0];

					marker.destroy();
				}
				break;

			case "dad":
				if (data.name === "map") {
					const { clientX, clientY } = data.event;

					this._lastClick.x = clientX;
					this._lastClick.y = clientY;
				}

				if (this._dadData && data.name === "map") {
					this._dadData = null;
					// prijde dalsi event, nechceme dad element zase pridat
					this._dadTo = setTimeout(() => {
						this._dadTo = 0;
					}, 350) as unknown as number;
				} else if (data.name === "element" && !this._dadTo) {
					const dataId = this._mapping.getWasmToStoreId(data.wasmId);
					const marker = this._markers.filter(item => item.id === dataId)[0];

					this._dadData = {
						wasmId: data.wasmId,
						id: dataId,
						marker,
					};
				}
				break;

			default:
		}
	}

	_clearAll() {
		for (const marker of this._markers) {
			marker.destroy();
		}

		this._markers = [];

		for (const geometry of this._geometries) {
			geometry.destroy();
		}

		this._geometries = [];
	}

	_test() {
		for (const geometry of this._geometries) {
			const points = geometry.data.points.slice();
			const lastPointY = points[points.length - 1];
			const lastPointX = points[points.length - 2];

			points.push(lastPointX + 0.01);
			points.push(lastPointY + 0.01);

			geometry.update({
				points,
			});
		}
	}

	_test2() {
		const lastMarker = this._markers[this._markers.length - 1];

		lastMarker.update({
			img: "/img/logo-big.png",
		});
	}

	async _test3() {
		const imgData = await this._wasm.getScreenshot();
		const img = new Image();

		img.addEventListener("load", () => {
			const width = img.naturalWidth / devicePixelRatio >>> 0;
			const height = img.naturalHeight / devicePixelRatio >>> 0;

			Object.assign(img.style, {
				"position": "absolute",
				"inset": "0 0 0 0",
				"z-index": 100,
				"width": `${width}px`,
				"height": `${height}px`,
			});
			document.body.append(img);
		});
		img.src = imgData;
		img.addEventListener("click", () => {
			img.remove();
		});
	}

	_test4() {
		const dataId = this._mapping.getWasmToStoreId(1);
		const marker = this._markers.filter(item => item.id === dataId)[0];

		marker.update({
			active: false,
		});
	}

	_contextMenu(event: PointerEvent) {
		event.preventDefault();
		event.stopPropagation();

		const { clientX, clientY } = event as MouseEvent;
		const pos = this._wasm.exports.mapControl.screenPosToMapPos({
			x: clientX,
			y: clientY,
		});
		const wgs = this._wasm.exports.module.GeoMath.mercatorToWGS(pos);
		const referenceElement = {
			getBoundingClientRect() {
				return {
					width: 0,
					height: 0,
					x: clientX,
					y: clientY,
					top: clientY,
					left: clientX,
					right: clientX,
					bottom: clientY,
				};
			},
		};

		const contextMenu = new ContextMenu({
			anchor: referenceElement as HTMLElement,
			position: "bottom-start",
			clickOutIgnoredElements: [],
		});

		contextMenu.popOver.append(new ContextMenuItem({
			text: `menu na souřadnicích ${wgs.x.toFixed(6)}, ${wgs.y.toFixed(6)}`,
			icon: createSDSIcon({ symbol: ICON_EXCLAMATION_CIRCLE_24 }),
			onClick: () => {
				contextMenu.close();
			},
		}).container);

		contextMenu.open();
	}
}

export function createWasm() {
	/* eslint-disable-next-line */
	new WasmController();
}
